import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck, FileText } from "lucide-react";
import Logo from "@/components/Logo";
import { reingresoSend, reingresoVerify, reingresoSeleccionar } from "@/lib/api";
import { toast } from "sonner";

export default function Reingreso() {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone | code | list
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [tramites, setTramites] = useState([]);
  const [msg, setMsg] = useState("");
  const [emailMasked, setEmailMasked] = useState("");

  const send = async () => {
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Ingresa un celular válido de 10 dígitos.");
    setBusy(true);
    try { const res = await reingresoSend(phone); setEmailMasked(res?.email_masked || ""); setStep("code"); toast.success("Te enviamos un código a tu correo."); }
    catch (e) { toast.error(e?.response?.data?.detail || "No pudimos enviar el código. Intenta de nuevo."); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const res = await reingresoVerify(phone, code);
      if (!res.approved) return toast.error(res.detail || "El código es incorrecto o expiró.");
      if (!res.tramites?.length) {
        setMsg("No encontramos un trámite asociado a este número. Si acabas de registrarte, puede tardar unos minutos en aparecer, o puedes iniciar un nuevo proceso.");
        setStep("list"); setTramites([]);
      } else { setTramites(res.tramites); setStep("list"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "No pudimos validar el código."); }
    finally { setBusy(false); }
  };

  const pick = async (tid) => {
    setBusy(true);
    try { await reingresoSeleccionar(tid); navigate(`/tramite/${tid}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "No pudimos abrir el trámite."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--af-black)" }}>
      <div className="af-container flex items-center gap-3" style={{ height: 68, borderBottom: "1px solid var(--af-border-dark)" }}>
        <button data-testid="reingreso-back" onClick={() => navigate("/")} className="text-white/80 hover:text-af-orange-core"><ArrowLeft size={22} /></button>
        <Link to="/"><Logo height={40} /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="af-card p-8 max-w-md w-full" data-testid="reingreso-page">
          <div className="mx-auto rounded-full flex items-center justify-center mb-5" style={{ width: 56, height: 56, background: "rgba(232,93,4,0.15)", border: "1px solid rgba(232,93,4,0.4)" }}>
            <ShieldCheck className="text-af-orange-core" size={28} />
          </div>
          <h1 className="h-impact text-white text-2xl text-center">Ya tengo un trámite</h1>

          {step === "phone" && (
            <div className="mt-6">
              <p className="text-white/65 text-sm text-center mb-4">Ingresa tu celular para consultar tu trámite de forma segura. Te enviaremos un código a tu correo.</p>
              <input data-testid="reingreso-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="af-input" placeholder="Celular (10 dígitos)" inputMode="tel" />
              <button data-testid="reingreso-send" onClick={send} disabled={busy} className="af-btn-primary w-full mt-4">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Enviar código"}
              </button>
            </div>
          )}

          {step === "code" && (
            <div className="mt-6">
              <p className="text-white/65 text-sm text-center mb-4">Ingresa el código que enviamos a tu correo{emailMasked ? ` ${emailMasked}` : ""}.</p>
              <input data-testid="reingreso-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))} className="af-input text-center tracking-[0.4em]" placeholder="Código" inputMode="numeric" />
              <button data-testid="reingreso-verify" onClick={verify} disabled={busy} className="af-btn-primary w-full mt-4">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Confirmar"}
              </button>
              <button onClick={send} disabled={busy} className="text-af-orange-core text-sm mt-3 w-full hover:underline">Reenviar código</button>
            </div>
          )}

          {step === "list" && (
            <div className="mt-6">
              {tramites.length === 0 ? (
                <>
                  <p className="text-white/70 text-sm text-center">{msg}</p>
                  <button onClick={() => navigate("/registro")} className="af-btn-primary w-full mt-5">Iniciar nuevo proceso</button>
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-white/65 text-sm text-center mb-2">Selecciona tu trámite:</p>
                  {tramites.map((tr) => (
                    <button key={tr.tramite_id} data-testid={`reingreso-tramite-${tr.tramite_id}`} onClick={() => pick(tr.tramite_id)} disabled={busy}
                      className="af-card p-4 w-full text-left hover:border-af-orange-core transition-colors flex items-center gap-3">
                      <FileText className="text-af-orange-core" size={22} />
                      <div>
                        <div className="text-white font-medium text-sm">{tr.modalidad} · {tr.vehiculo || "Vehículo por definir"}</div>
                        <div className="text-white/50 text-xs">Trámite {tr.tramite_id.slice(0, 8)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
